/**
 * useWithYou hook
 *
 * Lightweight state for the With You feature: preferences (load + update),
 * starting a session, and persisting an entry against the active session.
 */

import { useCallback, useState } from 'react'
import { withYouService, type WithYouPreferences, type WithYouSession } from '../services/withYouService'

export function useWithYou() {
  const [preferences, setPreferences] = useState<WithYouPreferences | null>(null)
  const [loadingPrefs, setLoadingPrefs] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeSession, setActiveSession] = useState<WithYouSession | null>(null)

  const loadPreferences = useCallback(async () => {
    setLoadingPrefs(true)
    setError(null)
    try {
      const prefs = await withYouService.getPreferences()
      setPreferences(prefs)
    } catch (e: any) {
      setError(e?.message || String(e))
    } finally {
      setLoadingPrefs(false)
    }
  }, [])

  const updatePreferences = useCallback(async (updates: Partial<WithYouPreferences>) => {
    setError(null)
    try {
      const prefs = await withYouService.updatePreferences(updates)
      setPreferences(prefs)
      return prefs
    } catch (e: any) {
      setError(e?.message || String(e))
      throw e
    }
  }, [])

  const startSession = useCallback(
    async (entry_source: 'push' | 'manual' | 'suggested' | 'followup' = 'manual') => {
      const session = await withYouService.startSession({ entry_source })
      setActiveSession(session)
      return session
    },
    [],
  )

  const recordEntry = useCallback(
    async (raw_text: string, sessionId?: string | number) => {
      const sid = sessionId ?? activeSession?.session_id
      if (!sid) throw new Error('No active With You session')
      return withYouService.recordEntry({ session_id: sid, raw_text })
    },
    [activeSession],
  )

  return {
    preferences,
    loadingPrefs,
    error,
    activeSession,
    setActiveSession,
    loadPreferences,
    updatePreferences,
    startSession,
    recordEntry,
  }
}
